import Coordinate from "../Coordinates/Coordinate.js";
import WGS84, {WGS84System} from "../Coordinates/WGS84.js";
import CoordinateConverter from "./CoordinateConverter.js";
import Container from "../Main/Container.js";
import {createSvgEl, starSvg} from "../RouteTechniques/Util/Svg.js";

export default class MapNorthSvgGraphic {
    static computeMeridianConvergence(coord: Coordinate): number {
        // TODO: Temporary check
        if (coord.code !== 'EPSG:28992' && coord.code !== 'EPSG:25832') {
            throw new Error();
        }

        const origSystem = <WGS84System>CoordinateConverter.getCoordinateSystem(coord.code);
        const wgs84System = <WGS84System>CoordinateConverter.getCoordinateSystem('EPSG:4326');

        const coordWgs = CoordinateConverter.convert(coord, wgs84System);
        const coordWgs2 = new WGS84(coordWgs.lng, coordWgs.lat + 1e-6);
        const coord2 = CoordinateConverter.convert(coordWgs2, origSystem);

        const dx = coord2.getX() - coord.getX();
        const dy = coord2.getY() - coord.getY();

        return -Math.atan2(dx, dy) / Math.PI * 180;
    }

    private static normalize180(degrees: number): number {
        if (degrees > 180) {
            return degrees - 360;
        } else {
            return degrees;
        }
    }

    static async buildNorthSvgGraphic(coord: Coordinate, image_height_px: number = 100): Promise<{svg: SVGSVGElement, magneticDeclination: number, meridianConvergence: number, gridDeclination: number, aspectRatio: number}> {
        const wgs84System = <WGS84System>CoordinateConverter.getCoordinateSystem('EPSG:4326');
        const coordWgs = CoordinateConverter.convert(coord, wgs84System);

        // Angle between Magnetic North and True North (Positive = MN to the right/clockwise of TN)
        const magneticDeclination = MapNorthSvgGraphic.normalize180((await Container.getMagneticNorthProvider()).retrieve(coordWgs.lng, coordWgs.lat).dec);

        // Angle between Grid North and True North (Positive = GN tot the right/clockwise of TN)
        const meridianConvergence = MapNorthSvgGraphic.normalize180(MapNorthSvgGraphic.computeMeridianConvergence(coord));

        const gridDeclination = magneticDeclination - meridianConvergence;

        // Angle between Magnetic North and Grid North
        const trueMinusGrid = -meridianConvergence;
        const magneticMinusGrid = magneticDeclination - meridianConvergence;

        // const magneticDeclinationRad = magneticDeclination / 180. * Math.PI;
        // const meridianConvergenceRad = meridianConvergence / 180. * Math.PI;

        let trueNorthPresentationAngleDeg: number, magneticNorthPresentationAngleDeg: number;
        const largePresentationAngleDeg = 20; // 30;
        const smallPresentationAngleDeg = 10; // 15;
        if (trueMinusGrid < magneticMinusGrid && magneticMinusGrid < 0) {
            trueNorthPresentationAngleDeg = -largePresentationAngleDeg;
            magneticNorthPresentationAngleDeg = -smallPresentationAngleDeg;
        } else if (trueMinusGrid < 0 && 0 <= magneticMinusGrid) {
            trueNorthPresentationAngleDeg = -smallPresentationAngleDeg;
            magneticNorthPresentationAngleDeg = smallPresentationAngleDeg;
        } else if (0 <= trueMinusGrid && trueMinusGrid < magneticMinusGrid) {
            trueNorthPresentationAngleDeg = smallPresentationAngleDeg;
            magneticNorthPresentationAngleDeg = largePresentationAngleDeg;
        } else if (magneticMinusGrid < trueMinusGrid && trueMinusGrid < 0) {
            trueNorthPresentationAngleDeg = -smallPresentationAngleDeg;
            magneticNorthPresentationAngleDeg = -largePresentationAngleDeg;
        } else if (magneticMinusGrid < 0 && 0 <= trueMinusGrid) {
            trueNorthPresentationAngleDeg = smallPresentationAngleDeg;
            magneticNorthPresentationAngleDeg = -smallPresentationAngleDeg;
        } else if (0 <= magneticMinusGrid && magneticMinusGrid < trueMinusGrid) {
            trueNorthPresentationAngleDeg = largePresentationAngleDeg;
            magneticNorthPresentationAngleDeg = smallPresentationAngleDeg;
        } else {
            throw new Error('Invalid combination of angles: ' + trueMinusGrid + '; ' + magneticMinusGrid);
        }
        const trueNorthPresentationAngleRad = trueNorthPresentationAngleDeg / 180. * Math.PI;
        const magneticNorthPresentationAngleRad = magneticNorthPresentationAngleDeg / 180. * Math.PI;
        const eps = 1.;

        const height = 1;
        const width = Math.sin(largePresentationAngleDeg / 180. * Math.PI);
        const minPresentationAngle = Math.min(trueNorthPresentationAngleDeg, magneticNorthPresentationAngleDeg);
        const paddingTop = 0;
        const paddingLeft = .06;
        const paddingRight = .06;
        const paddingBottom = 0;
        let x_start: number = null;
        if (Math.abs(minPresentationAngle - (smallPresentationAngleDeg)) < eps) {
            x_start = 0;
        } else if (Math.abs(minPresentationAngle - (-smallPresentationAngleDeg)) < eps) {
            x_start = -width / 2;
        } else if (Math.abs(minPresentationAngle - (-largePresentationAngleDeg)) < eps) {
            x_start = -width;
        } else {
            throw Error('Invalid minPresentationAngle');
        }
        const svg = createSvgEl('svg', {
            width: (image_height_px * width / height) + 'px',
            height: image_height_px + 'px',
            viewBox: (x_start - paddingLeft) + " " + (0 - height - paddingTop) + " " + (width + (paddingLeft + paddingRight)) + " " + (height + (paddingTop + paddingBottom)),
        });

        const group = createSvgEl('g', {
            // transform: 'translate(0, 1) scale(1, -1)',
        });
        const invert_y = -1;

        // Grid North
        group.appendChild(createSvgEl('line', {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: (1 - .04) * invert_y,
            stroke: '#000000',
            strokeWidth: 0.005,
        }));

        const gn_text = createSvgEl('text', {
            x: 0,
            y: (1 - .02) * invert_y,
            textAnchor: 'middle',
            dominantBaseline: 'text-bottom',
        });
        gn_text.textContent = 'GN';
        gn_text.style.font = '.08px sans-serif';
        group.appendChild(gn_text);

        // True North
        const tn_length = .7;
        const tn_c = Math.cos(trueNorthPresentationAngleRad);
        const tn_s = Math.sin(trueNorthPresentationAngleRad);
        group.appendChild(createSvgEl('line', {
            x1: 0,
            y1: 0,
            x2: tn_s * tn_length,
            y2: tn_c * tn_length * invert_y,
            stroke: '#000000',
            strokeWidth: 0.005,
        }));

        const tn_text = createSvgEl('text', {
            x: tn_s * (tn_length + .06),
            y: tn_c * (tn_length + .06) * invert_y,
            textAnchor: 'middle',
            dominantBaseline: 'text-bottom',
        });
        tn_text.textContent = 'WN';
        tn_text.style.font = '.08px sans-serif';
        group.appendChild(tn_text);

        group.appendChild(starSvg(
            tn_s * tn_length,
            tn_c * tn_length * invert_y,
            'extrapolate',
            .05,
            .025,
        ));

        // Magnetic North
        const mn_length = .95;
        const mn_c = Math.cos(magneticNorthPresentationAngleRad);
        const mn_s = Math.sin(magneticNorthPresentationAngleRad);
        group.appendChild(createSvgEl('line', {
            x1: 0,
            y1: 0,
            x2: mn_s * mn_length,
            y2: mn_c * mn_length * invert_y,
            stroke: '#000000',
            strokeWidth: 0.005,
        }));

        const mn_text = createSvgEl('text', {
            x: mn_s * (mn_length + .02),
            y: mn_c * (mn_length + .02) * invert_y,
            textAnchor: 'middle',
            dominantBaseline: 'text-bottom',
        });
        mn_text.textContent = 'MN';
        mn_text.style.font = '.08px sans-serif';
        group.appendChild(mn_text);

        group.appendChild(createSvgEl('polygon', {
            points: [
                [mn_s * mn_length, mn_c * mn_length * invert_y],
                [mn_s * mn_length * .85 - mn_c * .06, (mn_c * mn_length * .85 + mn_s * .06) * invert_y],
                [mn_s * mn_length * .9, mn_c * mn_length * .9 * invert_y],
            ].map(c => c[0] + ',' + c[1]).join(' '),
            stroke: 'none',
            fill: '#000000',
        }));

        // Arcs
        const wn_middle = Math.abs(Math.abs(trueNorthPresentationAngleDeg) - smallPresentationAngleDeg) < eps
            && Math.abs(Math.abs(magneticNorthPresentationAngleDeg) - largePresentationAngleDeg) < eps;

        svg.appendChild(MapNorthSvgGraphic.createDeltaArcPath(
            trueNorthPresentationAngleRad,
            magneticNorthPresentationAngleRad,
            wn_middle ? .47 : .55,
            'a',
            Math.abs(Math.abs(trueNorthPresentationAngleDeg - magneticNorthPresentationAngleDeg) - largePresentationAngleDeg) < eps,
        ));

        svg.appendChild(MapNorthSvgGraphic.createDeltaArcPath(
            0,
            magneticNorthPresentationAngleRad,
            wn_middle ? .6 : .7,
            'b',
            Math.abs(Math.abs(magneticNorthPresentationAngleDeg) - largePresentationAngleDeg) < eps,
        ));

        svg.appendChild(MapNorthSvgGraphic.createDeltaArcPath(
            trueNorthPresentationAngleRad,
            0,
            .4,
            'c',
            Math.abs(Math.abs(trueNorthPresentationAngleDeg) - largePresentationAngleDeg) < eps,
        ));

        svg.appendChild(group);

        return {
            svg,
            meridianConvergence,
            magneticDeclination,
            gridDeclination,
            aspectRatio: width / height,
        };
    }

    private static createDeltaArcPath(angle_rad1: number, angle_rad2: number, radius: number, text_content: string, large: boolean) {
        const invert_y = -1;
        const c1 = Math.cos(angle_rad1);
        const s1 = Math.sin(angle_rad1);
        const c2 = Math.cos(angle_rad2);
        const s2 = Math.sin(angle_rad2);

        const group = createSvgEl('g', {});

        const sweep = angle_rad1 < angle_rad2 ? 1 : 0;

        group.appendChild(createSvgEl('path', {
            d: 'M ' + (s1 * radius) + ' ' + (c1 * radius * invert_y) + ' A ' + radius + ' ' + radius + ' 0 0 ' + sweep + ' ' + (s2 * radius) + ' ' + (c2 * radius * invert_y),
            fill: 'none',
            stroke: '#000000',
            strokeWidth: 0.005,
            // strokeDasharray: ".03 .03",
        }));

        const angle_text = large ? (.75 * angle_rad1 + .25 * angle_rad2) : (.5 * angle_rad1 + .5 * angle_rad2);
        const c3 = Math.cos(angle_text);
        const s3 = Math.sin(angle_text);

        const text_node = createSvgEl('text', {
            x: s3 * (radius/* + .01*/),
            y: c3 * (radius/* + .01*/) * invert_y,
            textAnchor: 'middle',
            dominantBaseline: 'text-bottom',
        });
        text_node.textContent = text_content;
        text_node.style.font = '.08px sans-serif';
        group.appendChild(text_node);

        return group;
    }
}
